import { Box, Chip, Typography } from '@mui/material';
import { Column, Route, Text, VerticalLine } from './TravelDetailStyles';
import { useTranslation } from 'react-i18next';
import {
  ExpenseClaimRouteResponse,
  TravelMovementResponse
} from 'generated/index';
import { formatDistance, formatDistanceRaw } from '../utils';
import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@mui/material';

type Props = {
  route: ExpenseClaimRouteResponse | TravelMovementResponse;
  isTravelMovements?: boolean;
  isLast: boolean;
  i: number;
  isManual?: boolean;
  isCorrected?: boolean;
  isEditMode?: boolean;
  editedDistance?: number;
  onDistanceChange?: (index: number, value: string) => void;
};

export function isTravelMovementResponse(
  route: ExpenseClaimRouteResponse | TravelMovementResponse
): route is TravelMovementResponse {
  return 'fromDescription' in route && 'toAddress' in route;
}

export function isExpenseClaimRouteResponse(
  route: ExpenseClaimRouteResponse | TravelMovementResponse
): route is ExpenseClaimRouteResponse {
  return 'from' in route && 'to' in route;
}

const TravelDetailDiaglogRoutes = ({
  route,
  isTravelMovements,
  isLast,
  i,
  isManual,
  isEditMode,
  editedDistance,
  onDistanceChange
}: Props) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  const renderTravelMovementContent = (route: TravelMovementResponse) => (
    <>
      {isManual ? (
        <Typography variant="h1">{route.fromAddress}</Typography>
      ) : (
        <>
          <Typography variant="h1">{route.fromDescription}</Typography>
          <Text sx={{ textTransform: 'capitalize' }}>{route.fromAddress}</Text>
        </>
      )}
    </>
  );

  const renderExpenseClaimContent = (route: ExpenseClaimRouteResponse) => (
    <>
      <Typography variant="h1">{route.from}</Typography>
    </>
  );

  return (
    <Box key={i} ml={3} position="relative">
      <Route isTravelMovements={isTravelMovements}>
        <Column>
          {isTravelMovementResponse(route) &&
            renderTravelMovementContent(route)}
          {isExpenseClaimRouteResponse(route) &&
            renderExpenseClaimContent(route)}
        </Column>
        <VerticalLine
          isTravelMovements={isTravelMovements}
          isManual={isManual}
          isEditMode={isEditMode}
        />
      </Route>

      {route.distanceInMeters > 0 && (
        <Text
          sx={{
            position: 'relative',
            top: isTravelMovements ? '-12px' : '-15px',
            display: 'flex',
            alignItems: 'center',
            left: '2.5px',
            gap: '4px',
            lineHeight: 0
          }}
        >
          {isEditMode ? (
            <Controller
              name={`travelMovements[${i}].correctedDistanceInMeters`}
              control={control}
              render={() => (
                <TextField
                  label={t('travelMovements.distance')}
                  value={
                    editedDistance ??
                    (route.correctedDistanceInMeters
                      ? formatDistanceRaw(route.correctedDistanceInMeters)
                      : formatDistanceRaw(route.distanceInMeters))
                  }
                  onChange={(e) => onDistanceChange?.(i, e.target.value)}
                  type="number"
                  inputProps={{
                    step: 0.1,
                    min: 0
                  }}
                  variant="outlined"
                  size="small"
                  sx={{
                    maxWidth: '100px'
                  }}
                />
              )}
            />
          ) : (
            <>
              {route.correctedDistanceInMeters ? (
                <Box
                  sx={{
                    fontFamily: 'signikasemibold'
                  }}
                >
                  {formatDistance(route.correctedDistanceInMeters)}
                </Box>
              ) : (
                formatDistance(route.distanceInMeters)
              )}
            </>
          )}

          {isTravelMovementResponse(route) && route.type !== null && (
            <Chip
              label={t(`travelMovements.type.${route.type}`)}
              size="small"
              variant="filled"
              color={route.type === 'PRIVATE' ? 'primary' : 'info'}
              sx={{
                lineHeight: 'normal',
                marginLeft: '4px'
              }}
            />
          )}

          {isTravelMovementResponse(route) &&
            route.correctedDistanceInMeters && (
              <Chip
                label={`${t('travelMovements.original')}: ${formatDistance(
                  route.distanceInMeters
                )}`}
                size="small"
                variant="filled"
                color="default"
                sx={{
                  lineHeight: 'normal',
                  marginLeft: '4px'
                }}
              />
            )}
        </Text>
      )}

      {isLast && (
        <Route isTravelMovements={isTravelMovements}>
          <Column>
            {isTravelMovementResponse(route) && !isManual ? (
              <Typography variant="h1">{route.toDescription}</Typography>
            ) : (
              isExpenseClaimRouteResponse(route) && (
                <Typography variant="h1">{route.to}</Typography>
              )
            )}
            {isTravelMovementResponse(route) && isManual && (
              <Typography variant="h1">{route.toAddress}</Typography>
            )}
            {isTravelMovementResponse(route) && !isManual && (
              <Text sx={{ textTransform: 'capitalize' }}>
                {route.toAddress}
              </Text>
            )}
          </Column>
          <VerticalLine
            isTravelMovements={isTravelMovements}
            isManual={isManual}
            isEditMode={isEditMode}
            $last={isLast}
          />
        </Route>
      )}
    </Box>
  );
};

export default TravelDetailDiaglogRoutes;
